// src/pages/motherhood/MotherhoodPages.jsx
// Routes:
//   /demo/motherhood/myjourney  → MotherhoodMyJourney
//   /demo/motherhood/health     → MotherhoodHealthRecord
//   /demo/motherhood/community  → MotherhoodCommunity
//   /demo/motherhood/connect    → MotherhoodConnect
//   /demo/motherhood/events     → MotherhoodEvents
//   /demo/motherhood/products   → MotherhoodProducts
//
// Add to App.js:
//   import { MotherhoodMyJourney, MotherhoodHealthRecord, MotherhoodCommunity,
//            MotherhoodConnect, MotherhoodEvents, MotherhoodProducts } from './pages/motherhood/MotherhoodPages';
//
//   <Route path="/demo/motherhood/myjourney" element={<MotherhoodMyJourney />} />
//   <Route path="/demo/motherhood/health"    element={<MotherhoodHealthRecord />} />
//   <Route path="/demo/motherhood/community" element={<MotherhoodCommunity />} />
//   <Route path="/demo/motherhood/connect"   element={<MotherhoodConnect />} />
//   <Route path="/demo/motherhood/events"    element={<MotherhoodEvents />} />
//   <Route path="/demo/motherhood/products"  element={<MotherhoodProducts />} />

import { useState } from "react";
import NovaAvatarImg from "../../components/NovaAvatarImg";

// Motherhood: --purple / --purple-l / --purple-d
const C = {
  primary: "var(--purple)", primaryL: "var(--purple-l)", primaryM: "var(--purple-m)", primaryDk: "var(--purple-d)",
  sand: "var(--sand)", sand2: "var(--sand-2)", white: "var(--card)", border: "var(--border)",
  muted: "var(--muted)", text: "var(--text)", text2: "var(--text-2)",
  sage: "var(--sage)", sageL: "var(--sage-l)", sageM: "var(--sage-m)", sageD: "var(--sage-d)",
  pink: "var(--pink)", pinkL: "var(--pink-l)", pinkM: "var(--pink-m)", pinkDk: "var(--pink-dk)",
  amL: "var(--amber-l)", am: "var(--amber)", r: "var(--r)",
};

const Card = ({children,style={}}) => <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:C.r,padding:16,marginBottom:10,...style}}>{children}</div>;
const SLabel = ({children}) => <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:C.muted,marginBottom:10}}>{children}</div>;
const Btn = ({children,variant="primary",style={},onClick}) => {
  const vs={primary:{background:C.primary,color:"#fff",border:"none"},ghost:{background:C.primaryL,color:C.primaryDk,border:`1px solid ${C.primaryM}`},sand:{background:C.sand,color:C.muted,border:`1px solid ${C.border}`}};
  return <button onClick={onClick} style={{fontFamily:"inherit",fontSize:12,fontWeight:700,borderRadius:999,padding:"7px 16px",cursor:"pointer",...vs[variant],...style}}>{children}</button>;
};
const Tag = ({children,bg=C.primaryL,color=C.primaryDk,border=C.primaryM}) => <span style={{fontSize:10,fontWeight:600,padding:"2px 9px",borderRadius:999,background:bg,color,border:`1px solid ${border}`,display:"inline-block"}}>{children}</span>;
const NovaBanner = ({text,prompts=[]}) => (
  <div style={{background:C.primaryL,border:`1px solid ${C.primaryM}`,borderRadius:C.r,padding:"12px 14px",display:"flex",gap:10,alignItems:"flex-start",marginBottom:14}}>
    <NovaAvatarImg size={28} alt="" />
    <div style={{flex:1}}>
      <div style={{fontSize:12,color:C.primaryDk,lineHeight:1.55,marginBottom:prompts.length?8:0}}>{text}</div>
      {prompts.length>0&&<div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{prompts.map(p=><button key={p} style={{fontFamily:"inherit",fontSize:11,padding:"4px 10px",background:C.white,border:`1px solid ${C.primaryM}`,borderRadius:999,cursor:"pointer",color:C.primaryDk}}>{p}</button>)}</div>}
    </div>
    <button style={{fontFamily:"inherit",fontSize:11,fontWeight:600,color:C.primary,background:"none",border:"none",cursor:"pointer",whiteSpace:"nowrap"}}>Open Nova →</button>
  </div>
);

// PPD screening questions (Edinburgh scale simplified)
const PPD_QUESTIONS = [
  "I have been able to laugh and see the funny side of things",
  "I have looked forward with enjoyment to things",
  "I have blamed myself unnecessarily when things went wrong",
  "I have been anxious or worried for no good reason",
  "I have felt scared or panicky for no very good reason",
  "Things have been getting on top of me",
  "I have been so unhappy that I have had difficulty sleeping",
  "I have felt sad or miserable",
  "I have been so unhappy that I have been crying",
  "The thought of harming myself has occurred to me",
];

// ══════════════════════════════════════════════════════════════════════
// MOTHERHOOD MY JOURNEY
// ══════════════════════════════════════════════════════════════════════
export function MotherhoodMyJourney({ navigate }) {
  const nav = navigate || (() => {});
  const [tab,setTab]=useState("tracker");
  const [ppd,setPpd]=useState({});

  const MILESTONES_1M=[
    {age:"Birth",label:"Here!",done:true},
    {age:"2 weeks",label:"First check-up · weight regained?",done:true},
    {age:"1 month",label:"1-month pediatric visit · vaccines",done:true,current:true},
    {age:"2 months",label:"2-month visit · DTaP, Hib, PCV13, rotavirus",done:false},
    {age:"4 months",label:"4-month visit · more vaccines",done:false},
    {age:"6 months",label:"6-month visit · first foods",done:false},
  ];

  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif"}}>
      <div style={{padding:"0 0 24px"}}>

        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
          {[{label:"Baby's age",value:"6 wks",sub:"Born Feb 9, 2026"},{label:"Feeds today",value:"7",sub:"Last feed 45 min ago"},{label:"Sleep last night",value:"4.5 hrs",sub:"2 stretches"}].map(s=>(
            <div key={s.label} style={{background:C.sand,borderRadius:C.r,padding:"12px 14px"}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:4}}>{s.label}</div>
              <div style={{fontSize:22,fontWeight:700,color:C.text,letterSpacing:"-.02em"}}>{s.value}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:2}}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,marginBottom:14}}>
          {[["tracker","Daily log"],["baby","Baby tracker"],["ppd","PPD check-in"],["milestones","Milestones"],["feeding","Feeding"],["recovery","Recovery"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} style={{fontFamily:"inherit",fontSize:12,fontWeight:600,color:tab===id?C.primaryDk:C.muted,background:"none",border:"none",borderBottom:`2px solid ${tab===id?C.primary:"transparent"}`,padding:"10px 12px",cursor:"pointer",whiteSpace:"nowrap"}}>{label}</button>
          ))}
        </div>

        {/* DAILY LOG */}
        {tab==="tracker"&&(
          <div>
            <Card>
              <SLabel>📝 Today's log — Sofia, week 6</SLabel>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:8}}>How are YOU doing today? (Not the baby — you.)</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {[["😩","Struggling"],["😢","Tearful"],["😐","Getting through it"],["🙂","Okay"],["😊","Good"],["🤩","Great day"]].map(([e,l])=>(
                    <button key={l} style={{background:C.sand,border:`1px solid ${C.border}`,borderRadius:10,padding:"8px 10px",cursor:"pointer",textAlign:"center",minWidth:64}}>
                      <div style={{fontSize:20}}>{e}</div>
                      <div style={{fontSize:10,color:C.muted,marginTop:2}}>{l}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:8}}>Your symptoms today</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {["Exhausted","Overwhelmed","Anxious","Weepy","Numb","Sore / healing","Engorged","Headache","Back pain","Feeling disconnected","Intrusive thoughts","Actually okay"].map(s=>(
                    <button key={s} style={{fontSize:11,fontWeight:600,padding:"5px 11px",borderRadius:999,border:`1px solid ${C.border}`,background:C.sand,color:C.muted,cursor:"pointer"}}>{s}</button>
                  ))}
                </div>
              </div>
              <div style={{background:C.primaryL,borderRadius:C.r,padding:"10px 14px",marginBottom:14,fontSize:12,color:C.primaryDk,lineHeight:1.5}}>
                <strong>Nova note:</strong> It's week 6 — many women feel the hardest emotional dip around weeks 4–8. If you're struggling, this is very normal but also very worth telling your OB at your check-up this week. Nova can help you find the words.
              </div>
              <Btn variant="primary" style={{width:"100%"}}>Save today's log</Btn>
            </Card>
          </div>
        )}

        {/* BABY TRACKER */}
        {tab==="baby"&&(
          <div>
            <Card>
              <SLabel>Sofia's last 24 hours</SLabel>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
                {[{icon:"🍼",label:"Feeds",value:"7",sub:"Avg every 3.2 hrs"},{icon:"💤",label:"Sleep",value:"4.5 hrs",sub:"You got: 4.5 hrs"},{icon:"💧",label:"Wet diapers",value:"8",sub:"Normal 6–8/day"}].map(s=>(
                  <div key={s.label} style={{background:C.sand,borderRadius:C.r,padding:"10px 12px",textAlign:"center"}}>
                    <div style={{fontSize:20,marginBottom:4}}>{s.icon}</div>
                    <div style={{fontSize:10,color:C.muted}}>{s.label}</div>
                    <div style={{fontSize:16,fontWeight:700,color:C.text}}>{s.value}</div>
                    <div style={{fontSize:10,color:C.muted,marginTop:2}}>{s.sub}</div>
                  </div>
                ))}
              </div>
              <SLabel>Log a feed / sleep / diaper</SLabel>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[["🍼","Feed"],["💤","Sleep"],["💧","Diaper"],["📏","Weight"]].map(([icon,label])=>(
                  <button key={label} style={{fontFamily:"inherit",flex:1,padding:"12px 8px",background:C.sand,border:`1px solid ${C.border}`,borderRadius:C.r,cursor:"pointer",textAlign:"center"}}>
                    <div style={{fontSize:20}}>{icon}</div>
                    <div style={{fontSize:11,fontWeight:600,color:C.muted,marginTop:4}}>{label}</div>
                  </button>
                ))}
              </div>
              <SLabel>Weight chart</SLabel>
              <div style={{display:"flex",gap:6,alignItems:"flex-end",height:60,marginBottom:8}}>
                {[[7.1,"Birth"],[6.9,"Day 3"],[7.2,"Day 7"],[7.6,"Wk 2"],[8.1,"Wk 4"],[8.8,"Wk 6"]].map(([w,label],i)=>(
                  <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                    <div style={{fontSize:10,color:C.muted}}>{w}</div>
                    <div style={{width:"100%",background:i===5?C.primary:C.primaryM,borderRadius:"3px 3px 0 0",height:`${(w/9)*60}px`,opacity:i===5?1:0.5}}/>
                    <div style={{fontSize:10,color:C.muted}}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:11,color:C.primaryDk,background:C.primaryL,borderRadius:8,padding:"8px 12px"}}>Sofia has gained 1.7 lb since birth — right on track. Most babies double birth weight by 5 months.</div>
            </Card>
          </div>
        )}

        {/* PPD CHECK-IN */}
        {tab==="ppd"&&(
          <div>
            <Card>
              <SLabel>Edinburgh postnatal depression screen</SLabel>
              <div style={{background:C.primaryL,border:`1px solid ${C.primaryM}`,borderRadius:C.r,padding:"12px 14px",marginBottom:14,fontSize:12,color:C.primaryDk,lineHeight:1.6}}>
                Nova checks in with you on this every week for the first 6 months. This is not a diagnosis — it's a conversation starter. PPD affects 1 in 5 mothers and is highly treatable. Everything you share here is private.
              </div>
              <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:4}}>In the last 7 days...</div>
              {PPD_QUESTIONS.slice(0,5).map((q,i)=>(
                <div key={i} style={{padding:"10px 0",borderBottom:`1px solid ${C.sand2}`}}>
                  <div style={{fontSize:12,color:C.text,marginBottom:8,lineHeight:1.5}}>{q}</div>
                  <div style={{display:"flex",gap:6}}>
                    {["Never","Rarely","Sometimes","Often"].map(o=>(
                      <button key={o} onClick={()=>setPpd(p=>({...p,[i]:o}))} style={{fontFamily:"inherit",fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:999,border:`1px solid ${ppd[i]===o?C.primaryM:C.border}`,background:ppd[i]===o?C.primaryL:C.sand,color:ppd[i]===o?C.primaryDk:C.muted,cursor:"pointer"}}>{o}</button>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{padding:"12px 0",marginTop:4}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:12}}>+ 5 more questions</div>
                <Btn variant="primary" style={{width:"100%"}}>Submit & see Nova's read</Btn>
              </div>
              <div style={{fontSize:11,color:C.muted,marginTop:8,textAlign:"center"}}>
                If you're in crisis right now: <strong style={{color:C.primary}}>Postpartum Support International · 1-800-944-4773</strong>
              </div>
            </Card>
          </div>
        )}

        {/* MILESTONES */}
        {tab==="milestones"&&(
          <div>
            <Card>
              <SLabel>Sofia's milestones — month by month</SLabel>
              {MILESTONES_1M.map((m,i)=>(
                <div key={i} style={{display:"flex",gap:11,padding:"9px 0",borderBottom:i<MILESTONES_1M.length-1?`1px solid ${C.sand2}`:"none"}}>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                    <div style={{width:22,height:22,borderRadius:"50%",background:m.current?C.primary:m.done?C.primaryL:"#e8e8e8",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:m.current?"#fff":m.done?C.primaryDk:C.muted,flexShrink:0}}>
                      {m.current?"→":m.done?"✓":"·"}
                    </div>
                    {i<MILESTONES_1M.length-1&&<div style={{width:1,flex:1,background:C.border,margin:"2px 0"}}/>}
                  </div>
                  <div style={{flex:1,paddingTop:2}}>
                    <div style={{fontSize:11,fontWeight:700,color:m.current?C.primary:m.done?C.text:C.muted}}>{m.age}</div>
                    <div style={{fontSize:12,color:m.current?C.primaryDk:m.done?C.text2:C.muted,marginTop:1}}>{m.label}</div>
                  </div>
                  {m.current&&<Tag>Now</Tag>}
                </div>
              ))}
            </Card>
            <Card>
              <SLabel>Development — what to expect at 6 weeks</SLabel>
              {[["👁️","Vision","Can focus on faces 8–12 inches away. Tracks movement. Loves high-contrast patterns."],["😊","Social smile","May show first real smiles this week — responding to your face."],["💪","Muscle","Stronger neck — can lift head briefly during tummy time."],["🗣️","Communication","Cooing sounds starting. Responds to your voice."]].map(([icon,name,desc])=>(
                <div key={name} style={{display:"flex",gap:10,padding:"9px 0",borderBottom:`1px solid ${C.sand2}`}}>
                  <span style={{fontSize:20,flexShrink:0}}>{icon}</span>
                  <div><div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:2}}>{name}</div><div style={{fontSize:12,color:C.text2,lineHeight:1.5}}>{desc}</div></div>
                </div>
              ))}
              <div style={{marginTop:12,background:C.primaryL,borderRadius:C.r,padding:"10px 14px",fontSize:12,color:C.primaryDk}}>
                🚨 <strong>When to call your pediatrician:</strong> Not making eye contact by 8 weeks, not responding to loud sounds, or any feeding concerns you can't resolve.
              </div>
            </Card>
          </div>
        )}

        {/* FEEDING */}
        {tab==="feeding"&&(
          <div>
            <Card>
              <SLabel>Feeding tracker</SLabel>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {["Breastfeeding","Pumping","Formula","Combo"].map(f=>(
                  <button key={f} style={{fontFamily:"inherit",fontSize:12,fontWeight:600,flex:1,padding:"10px 8px",borderRadius:C.r,border:`1px solid ${f==="Breastfeeding"?C.primaryM:C.border}`,background:f==="Breastfeeding"?C.primaryL:C.sand,color:f==="Breastfeeding"?C.primaryDk:C.muted,cursor:"pointer"}}>{f}</button>
                ))}
              </div>
              <NovaBanner text="At 6 weeks, many mothers experience a growth spurt — Sofia may want to feed more often for 2–3 days. This is normal cluster feeding, not a supply problem." prompts={["Is my supply low?","How to relieve engorgement","When to introduce a bottle"]} />
              {[["Latch issues","Access video guides and community tips"],["Low supply concerns","What actually affects supply vs what doesn't"],["Breast pain / mastitis","Symptoms, treatment, when to call your doctor"],["Weaning support","When and how to wean at every stage"]].map(([title,desc])=>(
                <div key={title} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.sand2}`}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:C.text}}>{title}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:1}}>{desc}</div>
                  </div>
                  <Btn variant="ghost" style={{fontSize:11,padding:"4px 12px"}}>Read →</Btn>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* RECOVERY */}
        {tab==="recovery"&&(
          <div>
            <Card>
              <SLabel>Your postpartum recovery — week 6</SLabel>
              <div style={{background:C.primaryL,borderRadius:C.r,padding:"10px 14px",marginBottom:14,fontSize:12,color:C.primaryDk,lineHeight:1.55}}>
                Week 6 is when most providers give the "all clear" — but your body is still healing. Uterine involution takes 6 weeks, but pelvic floor recovery, hormonal rebalancing, and emotional processing can take months. Be honest with your OB this week.
              </div>
              {[["Perineal healing","Soreness, stitches, or tearing? Week 6 is when most discomfort should have resolved. If not, tell your OB.","Physical"],["Lochia (postpartum bleeding)","Should be light or stopped by week 6. Heavy fresh bleeding after week 3 needs a call to your provider.","Physical"],["Pelvic floor","Leaking, pressure, or pain during sex are common but not normal — pelvic PT referral is a week 6 conversation.","Physical"],["Hair loss","Postpartum hair shedding peaks weeks 8–16. Completely normal, driven by hormonal shift.","Physical"],["Mood & emotional health","Baby blues resolve by week 2. Anything continuing beyond that may be PPD — use the PPD check-in tab.","Emotional"],["Relationship changes","Intimacy, communication, and identity shifts are all normal and hard. Nova can help you find support or words.","Emotional"]].map(([title,desc,type])=>(
                <div key={title} style={{padding:"10px 0",borderBottom:`1px solid ${C.sand2}`}}>
                  <div style={{display:"flex",gap:6,marginBottom:4,alignItems:"center"}}>
                    <span style={{fontSize:12,fontWeight:700,color:C.text}}>{title}</span>
                    <Tag bg={type==="Emotional"?C.pinkL:C.primaryL} color={type==="Emotional"?C.pinkDk:C.primaryDk} border={type==="Emotional"?C.pinkM:C.primaryM} style={{fontSize:10}}>{type}</Tag>
                  </div>
                  <div style={{fontSize:12,color:C.text2,lineHeight:1.5}}>{desc}</div>
                </div>
              ))}
            </Card>
          </div>
        )}

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MOTHERHOOD HEALTH RECORD
// ══════════════════════════════════════════════════════════════════════
export function MotherhoodHealthRecord({ navigate }) {
  const nav = navigate || (() => {});
  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif"}}>
      <div style={{padding:"0 0 24px"}}>
        <NovaBanner text="Your 6-week postpartum check-up is this week. Nova has prepared a list of everything to raise — don't leave without discussing pelvic floor, mood, and contraception." prompts={["What to ask at my 6-week check-up","Is my healing normal?","PPD vs baby blues — what's the difference?"]} />

        <SLabel>Your postpartum health</SLabel>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:14}}>
          {[{label:"Weeks postpartum",value:"6",sub:"Born Feb 9",color:C.primary},{label:"Birth type",value:"Vaginal",sub:"No complications",color:C.text},{label:"PPD screen",value:"Low",sub:"Week 4 check",color:C.sage},{label:"Breastfeeding",value:"Active",sub:"7 feeds/day",color:C.primary}].map(s=>(
            <div key={s.label} style={{background:C.sand,borderRadius:C.r,padding:"12px 14px"}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:3}}>{s.label}</div>
              <div style={{fontSize:16,fontWeight:700,color:s.color}}>{s.value}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:2}}>{s.sub}</div>
            </div>
          ))}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <Card>
            <SLabel>Sofia's health record</SLabel>
            {[{label:"Birth weight",value:"7 lb 4 oz"},{label:"Current weight",value:"8 lb 12 oz (+1.5 lb)"},{label:"Length at birth",value:"20 inches"},{label:"Blood type",value:"O+"},{label:"Newborn screen",value:"All normal"},{label:"Hearing screen",value:"Passed"},{label:"Next vaccines",value:"2-month visit — DTaP, Hib, PCV13"}].map(r=>(
              <div key={r.label} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.sand2}`,fontSize:12}}>
                <span style={{color:C.muted}}>{r.label}</span>
                <span style={{fontWeight:600,color:C.text}}>{r.value}</span>
              </div>
            ))}
          </Card>
          <Card>
            <SLabel>Your 6-week check-up prep</SLabel>
            <div style={{background:C.primaryL,borderRadius:C.r,padding:"10px 12px",marginBottom:12,fontSize:12,color:C.primaryDk,lineHeight:1.5}}>Don't leave without covering these 5 things:</div>
            {["Pelvic floor — any leaking, pain, or pressure?","Mood check — honest answer, not 'I'm fine'","Contraception — what's right for breastfeeding?","Wound / healing check — perineum or scar","Sexual health — when and how to resume safely"].map((q,i)=>(
              <div key={i} style={{display:"flex",gap:8,padding:"6px 0",fontSize:12,color:C.text2,alignItems:"baseline",borderBottom:`1px solid ${C.sand2}`}}>
                <span style={{color:C.primary,fontWeight:700,flexShrink:0}}>{i+1}.</span>{q}
              </div>
            ))}
            <Btn variant="primary" style={{marginTop:12,width:"100%",fontSize:11}}>Ask Nova to help me prepare →</Btn>
          </Card>
        </div>

        <Card>
          <SLabel>Activities & milestone finder</SLabel>
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <input placeholder="Baby's age or ZIP code..." style={{fontFamily:"inherit",fontSize:12,padding:"8px 12px",borderRadius:999,border:`1px solid ${C.border}`,background:C.sand,flex:1}}/>
            <Btn variant="primary">Find activities</Btn>
          </div>
          {[{age:"0–3 months",name:"Tummy time & sensory play",loc:"At home · daily · free",desc:"10-minute tummy time sessions 2–3x/day. High-contrast cards, mirrors, black-and-white mobiles."},
            {age:"3–6 months",name:"Baby gym & music classes",loc:"Columbus Rec Center · Tuesdays",desc:"Wiggle & Giggle class — 30 mins of movement and song for babies 3–6 months. $8/class."},
            {age:"6–12 months",name:"Swim lessons — parent & tot",loc:"YMCA Columbus · Weekly",desc:"Water introduction, floating, kicking. Builds confidence and helps with sleep. $45/month."}
          ].map((a,i)=>(
            <div key={i} style={{padding:"10px 0",borderBottom:`1px solid ${C.sand2}`}}>
              <div style={{display:"flex",gap:8,marginBottom:4,alignItems:"flex-start"}}>
                <Tag bg={C.sand2} color={C.muted} border={C.border}>{a.age}</Tag>
                <div style={{fontSize:12,fontWeight:700,color:C.text}}>{a.name}</div>
              </div>
              <div style={{fontSize:11,color:C.primary,marginBottom:3}}>{a.loc}</div>
              <div style={{fontSize:12,color:C.text2,lineHeight:1.45}}>{a.desc}</div>
            </div>
          ))}
          <Btn variant="ghost" style={{width:"100%",marginTop:12}}>Find more activities near me →</Btn>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MOTHERHOOD COMMUNITY
// ══════════════════════════════════════════════════════════════════════
export function MotherhoodCommunity({ navigate }) {
  const nav = navigate || (() => {});
  const POSTS=[
    {init:"S",bg:C.primaryM,color:C.primaryDk,name:"Sofia M.",badge:"6 weeks",date:"3/23/2026",body:"Week 6 and I finally cried at my OB appointment when she asked how I was really doing — not how the baby was. She listened for 20 minutes and referred me to a maternal mental health therapist. I didn't know I could ask for that. Please ask. 💜",hearts:412,boats:287,replies:56},
    {init:"M",bg:C.pinkM,color:C.pinkDk,name:"Maya",badge:"friend",date:"3/23/2026",body:"Sofia had her first real smile today. I was so exhausted I almost missed it. Then it happened and I sobbed. These tiny moments are the whole thing. 🤱",hearts:567,boats:143,replies:34},
    {init:"J",bg:C.sageM,color:C.sageD,name:"Jamie R.",badge:null,date:"3/22/2026",body:"Nobody warned me about the postpartum hair loss. I'm 10 weeks out and losing SO much. Nova told me this peaks at weeks 8–16 and is totally hormonal. Just nice to know I'm not broken.",hearts:234,boats:389,replies:41},
  ];
  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif"}}>
      <div style={{padding:"12px 16px 6px",background:C.sand,borderBottom:`1px solid ${C.border}`}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:C.muted,marginBottom:2}}>Community · Motherhood</div>
        <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:2}}>Your motherhood community</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:10}}>🤱 Motherhood chapter — 9,400 members · 106 online right now</div>
      </div>
      <div style={{padding:14}}>
        <NovaBanner text="106 women online. The Fourth Trimester room has 22 women sharing right now — postpartum week 4–8. Your people." prompts={["Who's in the 4th trimester right now?","Find breastfeeding support","PPD — who else is dealing with this?"]} />
        <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:C.r,padding:"12px 14px",marginBottom:12}}>
          <div style={{display:"flex",gap:10}}>
            <div style={{width:36,height:36,borderRadius:"50%",background:C.primaryM,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:C.primaryDk}}>M</div>
            <input placeholder="Share something with your chapter..." style={{flex:1,fontFamily:"inherit",fontSize:13,border:`1px solid ${C.border}`,borderRadius:999,padding:"8px 14px",background:C.sand,color:C.muted}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:10}}>
            <div style={{display:"flex",gap:6}}><Btn variant="sand" style={{fontSize:11,padding:"4px 12px"}}>📷 Photo</Btn></div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}><Tag>🤱 Motherhood</Tag><Btn variant="primary" style={{fontSize:12,padding:"6px 16px"}}>Post</Btn></div>
          </div>
        </div>
        {POSTS.map((post,i)=>(
          <div key={i} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:C.r,padding:"13px 15px",marginBottom:10}}>
            <div style={{display:"flex",gap:9,marginBottom:8}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:post.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:post.color,flexShrink:0}}>{post.init}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>{post.name}{post.badge&&<span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:999,background:C.primaryL,color:C.primaryDk,border:`1px solid ${C.primaryM}`,marginLeft:6}}>{post.badge}</span>}</div>
                <div style={{fontSize:11,color:C.muted}}>{post.date}</div>
              </div>
            </div>
            <div style={{fontSize:13,color:C.text2,lineHeight:1.6,marginBottom:10}}>{post.body}</div>
            <div style={{display:"flex",borderTop:`1px solid ${C.sand2}`,paddingTop:7}}>
              <button style={{fontFamily:"inherit",fontSize:12,fontWeight:600,color:C.primary,background:"none",border:"none",flex:1,cursor:"pointer"}}>❤️ {post.hearts}</button>
              <button style={{fontFamily:"inherit",fontSize:12,fontWeight:600,color:C.primaryDk,background:"none",border:"none",flex:1,cursor:"pointer"}}>🤝 Same boat · {post.boats}</button>
              <button style={{fontFamily:"inherit",fontSize:12,fontWeight:600,color:C.muted,background:"none",border:"none",flex:1,cursor:"pointer"}}>💬 Reply · {post.replies}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MOTHERHOOD CONNECT
// ══════════════════════════════════════════════════════════════════════
export function MotherhoodConnect({ navigate }) {
  const nav = navigate || (() => {});
  const [tab,setTab]=useState("women");
  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif"}}>
      <div style={{display:"flex",borderBottom:`1px solid ${C.border}`}}>
        {[["women","💕 Moms like me"],["rooms","🟢 Live rooms"],["groups","👥 Groups"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{fontFamily:"inherit",fontSize:12,fontWeight:600,color:tab===id?C.primaryDk:C.muted,background:"none",border:"none",borderBottom:`2px solid ${tab===id?C.primary:"transparent"}`,padding:"11px 16px",cursor:"pointer",whiteSpace:"nowrap"}}>{label}</button>
        ))}
      </div>
      <div style={{padding:14}}>
        {tab==="women"&&(
          <div>
            <div style={{background:C.primaryL,border:`1px solid ${C.primaryM}`,borderRadius:C.r,padding:"14px 16px",marginBottom:14}}>
              <div style={{fontSize:16,fontWeight:700,color:C.primaryDk,marginBottom:3}}>Moms going through this with you</div>
              <div style={{fontSize:12,color:C.primaryDk}}>Matched on baby age · breastfeeding · Columbus, OH · week 6</div>
            </div>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:C.muted,marginBottom:10}}>Your closest match</div>
            <div style={{background:C.white,border:`2px solid ${C.primary}`,borderRadius:C.r,padding:16,marginBottom:10}}>
              <div style={{display:"flex",gap:10,marginBottom:8}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:C.primaryM,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:C.primaryDk}}>KL</div>
                <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700,color:C.text}}>Kara L.</div><div style={{fontSize:11,color:C.muted}}>Baby 7 weeks · breastfeeding · Columbus · vaginal birth</div></div>
                <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:999,background:C.primaryL,color:C.primaryDk,border:`1px solid ${C.primaryM}`}}>96% match</span>
              </div>
              <div style={{display:"flex",gap:5,marginBottom:10,flexWrap:"wrap"}}>
                <Tag>Same baby age</Tag>
                <Tag bg={C.sageL} color={C.sageD} border={C.sageM}>Same city</Tag>
                <Tag bg={C.pinkL} color={C.pinkDk} border={C.pinkM}>Breastfeeding</Tag>
              </div>
              <div style={{fontStyle:"italic",fontSize:12,color:C.text2,background:C.sand,borderRadius:8,padding:"8px 12px",borderLeft:`3px solid ${C.primaryM}`,marginBottom:12}}>"7 weeks postpartum and finally feel like I might survive. The first 5 weeks were the hardest thing I've ever done. Anyone else feel like nobody told them enough?"</div>
              <div style={{display:"flex",gap:8}}><Btn variant="primary" style={{flex:1}}>Connect</Btn><Btn variant="ghost" style={{flex:1}}>Message</Btn></div>
            </div>
          </div>
        )}
        {tab==="rooms"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[
              {name:"Fourth trimester — weeks 4–8",desc:"The hardest weeks, honestly",count:22,hot:true},
              {name:"Breastfeeding support",desc:"Latch, supply, pumping questions",count:17},
              {name:"PPD & maternal mental health",desc:"Safe, honest, no judgment",count:11},
              {name:"Sleep training & schedules",desc:"When, how, and whether to",count:19},
              {name:"Going back to work",desc:"Pumping at work, childcare, guilt",count:8},
              {name:"Moms of toddlers (1–3)",desc:"Tantrums, words, big feelings",count:14},
            ].map(room=>(
              <div key={room.name} style={{background:room.hot?C.primaryL:C.white,border:`1px solid ${room.hot?C.primaryM:C.border}`,borderRadius:C.r,padding:"13px 14px"}}>
                {room.hot&&<span style={{fontSize:10,fontWeight:700,background:C.primary,color:"#fff",padding:"2px 7px",borderRadius:999,display:"inline-block",marginBottom:6}}>Active now</span>}
                <div style={{fontSize:13,fontWeight:700,color:room.hot?C.primaryDk:C.text,marginBottom:4}}>{room.name}</div>
                <div style={{fontSize:11,color:room.hot?C.primaryDk:C.muted,marginBottom:10,lineHeight:1.4}}>{room.desc}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,color:C.primaryDk}}><div style={{width:6,height:6,borderRadius:"50%",background:"#22c55e"}}/>{room.count} online</div>
                  <Btn variant={room.hot?"primary":"sand"} style={{fontSize:11,padding:"4px 12px"}}>Join</Btn>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab==="groups"&&(
          <div>
            {[{icon:"👶",name:"Fourth Trimester Circle",meta:"2,400 members · recovery, breastfeeding, real talk",joined:true},{icon:"🤱",name:"Breastfeeding Support",meta:"3,100 members · latch, supply, pumping",joined:true},{icon:"🧠",name:"PPD Recovery Circle",meta:"1,600 members · postpartum mental health",joined:false},{icon:"🌟",name:"Moms of Tweens & Teens",meta:"2,800 members · navigating the older years",joined:false},{icon:"💼",name:"Working Moms Circle",meta:"1,900 members · balance, guilt, pumping at work",joined:false}].map(g=>(
              <div key={g.name} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 12px",background:C.white,border:`1px solid ${C.border}`,borderRadius:C.r,marginBottom:8}}>
                <div style={{width:38,height:38,borderRadius:10,background:C.primaryL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{g.icon}</div>
                <div style={{flex:1}}><div style={{fontSize:12,fontWeight:700,color:C.text}}>{g.name}</div><div style={{fontSize:11,color:C.muted}}>{g.meta}</div></div>
                {g.joined?<span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:999,background:C.primaryL,color:C.primaryDk,border:`1px solid ${C.primaryM}`}}>Joined</span>:<Btn variant="ghost" style={{fontSize:11,padding:"4px 12px"}}>Join →</Btn>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MOTHERHOOD EVENTS
// ══════════════════════════════════════════════════════════════════════
export function MotherhoodEvents({ navigate }) {
  const nav = navigate || (() => {});
  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif"}}>
      <div style={{padding:"0 0 24px"}}>
        <NovaBanner text="Nova found 5 motherhood events near Columbus this month — including a free new moms circle tomorrow and a lactation consultant drop-in this week." prompts={["Find a lactation consultant near me","PPD support groups Columbus","Baby music classes near me"]} />
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {["All","In person","Virtual","This week","This month","Baby age: 0–3m"].map(f=>(
            <button key={f} style={{fontFamily:"inherit",fontSize:11,fontWeight:600,padding:"5px 13px",borderRadius:999,border:`1px solid ${f==="All"?C.primaryM:C.border}`,background:f==="All"?C.primaryL:C.sand,color:f==="All"?C.primaryDk:C.muted,cursor:"pointer"}}>{f}</button>
          ))}
        </div>
        {[
          {date:"MAR\n24",title:"New Moms Circle — Columbus",org:"Postpartum Support International · Columbus",desc:"Free weekly group for moms 0–12 months postpartum. No agenda, no advice unless asked. Just honest company.",tags:["In person","Free","Nova matched"],hot:true},
          {date:"MAR\n25",title:"Lactation Consultant Drop-In",org:"Nationwide Children's · Columbus",desc:"Walk-in lactation support Tuesday afternoons. Latch issues, supply concerns, pumping prep. No appointment needed.",tags:["In person","$30","This week"]},
          {date:"MAR\n29",title:"Postpartum Fitness — Stroller Fitness",org:"Columbus Mom Network · Schiller Park",desc:"Gentle postpartum-safe movement with your baby in the stroller. Pelvic floor awareness built in. Instructor is a women's health PT.",tags:["In person","$15","Week 6+"]},
          {date:"APR\n5",title:"Maternal Mental Health — What to Know",org:"OrivonAI · Virtual",desc:"Free webinar on PPD, PPA, and postpartum rage. How to recognize it, how to talk about it, and what treatment looks like.",tags:["Virtual","Free","OrivonAI"]},
          {date:"APR\n8",title:"Wiggle & Giggle Baby Class",org:"Columbus Rec Center",desc:"Song, movement, and sensory play for babies 0–6 months with a parent. Builds connection and gets you out of the house.",tags:["In person","$8/class","0–6 months"]},
        ].map((ev,i)=>(
          <div key={i} style={{background:ev.hot?C.primaryL:C.white,border:`1px solid ${ev.hot?C.primaryM:C.border}`,borderRadius:C.r,padding:16,marginBottom:10,display:"flex",gap:14}}>
            <div style={{flexShrink:0,textAlign:"center",background:ev.hot?C.primary:C.sand,color:ev.hot?"#fff":C.text,borderRadius:10,padding:"8px 12px",minWidth:52}}>
              {ev.date.split("\n").map((l,j)=><div key={j} style={{fontSize:j===0?10:20,fontWeight:700,lineHeight:1.1}}>{l}</div>)}
            </div>
            <div style={{flex:1}}>
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:5}}>{ev.tags.map(t=><Tag key={t} bg={t==="Nova matched"?C.amL:C.sand2} color={t==="Nova matched"?C.am:C.muted} border={t==="Nova matched"?"#f5c97a":C.border}>{t}</Tag>)}</div>
              <div style={{fontSize:13,fontWeight:700,color:ev.hot?C.primaryDk:C.text,marginBottom:3}}>{ev.title}</div>
              <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{ev.org}</div>
              <div style={{fontSize:12,color:ev.hot?C.primaryDk:C.text2,lineHeight:1.5}}>{ev.desc}</div>
            </div>
            <Btn variant={ev.hot?"primary":"ghost"} style={{alignSelf:"flex-end",whiteSpace:"nowrap"}}>{ev.hot?"Join →":"Register"}</Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MOTHERHOOD PRODUCTS
// ══════════════════════════════════════════════════════════════════════
export function MotherhoodProducts({ navigate }) {
  const nav = navigate || (() => {});
  const [filter,setFilter]=useState("all");
  const PRODUCTS=[
    {brand:"Spectra",name:"Spectra S2 Hospital Grade Pump",desc:"The most recommended pump in the community. Closed system, quiet, dual motor. FSA/HSA eligible. Often covered by insurance.",price:"$160",tag:"Feeding",tagType:"feeding",digs:2134,rating:4.9,reviews:"41k",featured:true},
    {brand:"Lansinoh",name:"HPA Lanolin Nipple Cream",desc:"The original. Safe for baby, no need to wipe off. For cracked, sore nipples in the first weeks of breastfeeding.",price:"$10",tag:"Feeding",tagType:"feeding",digs:1876,rating:4.8,reviews:"38k"},
    {brand:"Snoo",name:"SNOO Smart Sleeper Bassinet",desc:"Responds to crying with motion and sound. AAP-approved safe sleep. Rent from $159/month instead of buying.",price:"$1699",tag:"Sleep",tagType:"sleep",digs:1243,rating:4.6,reviews:"12k"},
    {brand:"Hatch",name:"Hatch Rest+ Sound Machine",desc:"White noise, nightlight, and wake-up clock in one. Grows from newborn through toddler and beyond.",price:"$90",tag:"Sleep",tagType:"sleep",digs:987,rating:4.7,reviews:"18k"},
    {brand:"Elvie",name:"Elvie Stride Double Pump",desc:"Wearable, hands-free, silent. Fits in your bra. Game-changer for pumping at work or on the go.",price:"$230",tag:"Feeding",tagType:"feeding",digs:876,rating:4.5,reviews:"6k"},
    {brand:"Earth Mama",name:"Organic Nipple Butter",desc:"Lanolin-free alternative. Coconut oil and cocoa butter base. For breastfeeding and nipple care.",price:"$14",tag:"Postpartum care",tagType:"care",digs:543,rating:4.7,reviews:"8k"},
  ];
  const filtered=filter==="all"?PRODUCTS:PRODUCTS.filter(p=>p.tagType===filter);
  const tc={feeding:{bg:C.primaryL,color:C.primaryDk,border:C.primaryM},sleep:{bg:C.ppL,color:C.ppD,border:C.ppM},care:{bg:C.pinkL,color:C.pinkDk,border:C.pinkM}};

  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif"}}>
      <div style={{padding:"0 0 24px"}}>
        <NovaBanner text="At 6 weeks, the most-reached-for products in the community are: nipple cream, a wearable pump for hands-free feeding, and a white noise machine for longer sleep stretches." prompts={["Is this pump covered by my insurance?","Best products for cracked nipples","Sleep training products — when?"]} />
        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
          <span style={{fontSize:10,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:C.muted,marginRight:4}}>Filter</span>
          {[["all","All"],["feeding","Feeding"],["sleep","Sleep"],["care","Postpartum care"]].map(([id,label])=>(
            <button key={id} onClick={()=>setFilter(id)} style={{fontFamily:"inherit",fontSize:11,fontWeight:600,padding:"5px 14px",borderRadius:999,border:`1px solid ${filter===id?C.primaryM:C.border}`,background:filter===id?C.primaryL:C.sand,color:filter===id?C.primaryDk:C.muted,cursor:"pointer"}}>{label}</button>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:14}}>
          {filtered.map(p=>{
            const colors=tc[p.tagType]||{bg:C.sand2,color:C.muted,border:C.border};
            return (
              <div key={p.name} style={{background:C.white,border:`${p.featured?"2px":"1px"} solid ${p.featured?C.primary:C.border}`,borderRadius:C.r,overflow:"hidden",display:"flex",flexDirection:"column",position:"relative"}}>
                {p.featured&&<div style={{position:"absolute",top:-1,left:14,background:C.primary,color:"#fff",fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:"0 0 8px 8px"}}>Nova's top pick</div>}
                <div style={{height:120,background:C.sand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,position:"relative"}}>
                  🤱
                  <div style={{position:"absolute",top:10,left:10,background:"rgba(255,253,249,.92)",borderRadius:999,padding:"3px 8px",fontSize:10,fontWeight:700,color:C.text}}>★ {p.rating} <span style={{fontSize:10,color:C.muted}}>({p.reviews})</span></div>
                  <div style={{position:"absolute",bottom:8,right:8,background:C.primary,color:"#fff",fontSize:11,fontWeight:800,padding:"5px 11px",borderRadius:999}}>{p.digs} digs</div>
                </div>
                <div style={{padding:"12px 14px",flex:1,display:"flex",flexDirection:"column"}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:C.muted,marginBottom:3}}>{p.brand}</div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>{p.name}</div>
                  <div style={{fontSize:11,color:C.muted,lineHeight:1.45,flex:1,marginBottom:10}}>{p.desc}</div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontSize:16,fontWeight:700,color:C.text}}>{p.price}</div>
                    <span style={{fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:999,background:colors.bg,color:colors.color,border:`1px solid ${colors.border}`}}>{p.tag}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{background:C.primary,borderRadius:C.r,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
          <div><div style={{fontSize:13,fontWeight:600,color:"#fff"}}>Sign up to access the full motherhood catalog</div><div style={{fontSize:11,color:"rgba(255,255,255,0.8)",marginTop:2}}>200+ products for every stage — newborn through teen years</div></div>
          <button style={{fontFamily:"inherit",fontSize:12,fontWeight:700,color:C.primaryDk,background:"#fff",border:"none",borderRadius:999,padding:"8px 18px",cursor:"pointer",whiteSpace:"nowrap"}}>Sign up free →</button>
        </div>
      </div>
    </div>
  );
}
